import React, { useEffect, useRef, useState } from 'react';
import { Plus, Search, Sparkles, X } from 'lucide-react';
import { AudienceLevel, ConversationRead, LEVEL_LABELS, LEVEL_ORDER } from '../../types/pathway';
import { SelectDropdown } from '../SelectDropdown';
import { ResetFiltersButton } from '../ResetFiltersButton';
interface NotesInputProps {
  notes: string;
  audience: AudienceLevel | 'any';
  liveRead: ConversationRead;
  activeTerms: string[];
  suggestedTerms: string[];
  detectedLevel: AudienceLevel | null;
  hasSearch: boolean;
  onReset: () => void;
  onNotesChange: (value: string) => void;
  onAudienceChange: (value: AudienceLevel | 'any') => void;
  onToggleTerm: (term: string) => void;
  onClearNotes: () => void;
  onBrowseAll: () => void;
}
const EXAMPLE = 'Spoke with the VP of People at Meridian Health. They promoted 24 senior nurses into manager roles after a reorg and turnover has spiked. The new managers are still doing the clinical work themselves and avoiding performance conversations.';
function Field({
  label,
  hint,
  htmlFor,
  children
}: {
  label: string;
  hint?: React.ReactNode;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-1635" data-mp="3cujs60lknjud">
      <div className="mb-1 flex items-baseline justify-between gap-2" data-id="element-1636" data-mp="2yx3lcnzd5o23">
        <label htmlFor={htmlFor} className="block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1637" data-mp="2yz7wnqzldpce">
          {label}
        </label>
        {hint}
      </div>
      {children}
    </div>;
}

/** Compact conversation-notes entry: notes, role, extra terms, topic chips. */
export function NotesInput({
  notes,
  audience,
  liveRead,
  activeTerms,
  suggestedTerms,
  detectedLevel,
  hasSearch,
  onReset,
  onNotesChange,
  onAudienceChange,
  onToggleTerm,
  onClearNotes,
  onBrowseAll
}: NotesInputProps) {
  const typingRef = useRef<number | null>(null);
  const [draft, setDraft] = useState('');
  const [dismissedHeadcount, setDismissedHeadcount] = useState<number | null>(null);
  const showHeadcount = liveRead.headcount !== null && liveRead.headcount !== dismissedHeadcount;
  const detectedTerms = liveRead.terms;
  const stopTyping = () => {
    if (typingRef.current !== null) {
      window.clearInterval(typingRef.current);
      typingRef.current = null;
    }
  };
  useEffect(() => stopTyping, []);
  const typeExample = () => {
    stopTyping();
    setDismissedHeadcount(null);
    let index = 0;
    onNotesChange('');
    typingRef.current = window.setInterval(() => {
      index = Math.min(index + 4, EXAMPLE.length);
      onNotesChange(EXAMPLE.slice(0, index));
      if (index >= EXAMPLE.length) stopTyping();
    }, 16);
  };
  const clearNotes = () => {
    stopTyping();
    setDismissedHeadcount(null);
    onClearNotes();
  };
  const commitDraft = () => {
    draft.split(',').map(t => t.trim().toLowerCase()).filter(t => t && !activeTerms.includes(t)).forEach(onToggleTerm);
    setDraft('');
  };
  const fromNotesHint = detectedLevel && audience === detectedLevel ? <span className="inline-flex items-center gap-1 text-xs text-[#30718D]" data-id="element-1638" data-mp="1y0cosbd4elwp">
        <Sparkles size={11} aria-hidden="true" data-id="element-1639" data-mp="22ywfsq6kmnvx" />
        From notes
      </span> : null;
  return <div data-id="element-1640" data-mp="272udcwg8zqmv">
      <label htmlFor="conversation-notes" className="sr-only" data-id="element-1641" data-mp="2722hdkiqnqw2">
        What did the prospect tell you?
      </label>
      <textarea id="conversation-notes" value={notes} onChange={e => onNotesChange(e.target.value)} rows={3} placeholder="Paste call notes in plain language — modules match as you type." className="w-full resize-y rounded-lg border border-gray-300 bg-white p-3 text-sm text-gray-900 placeholder:text-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1642" data-mp="2byjgf9rh7pnx" />
      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1" data-id="element-1643" data-mp="vmxdid6vfmav">
        <button type="button" onClick={typeExample} className="text-xs font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1644" data-mp="10lgyaa9qas1n">
          Use an example
        </button>
        {notes.trim().length > 0 && <button type="button" onClick={clearNotes} className="text-xs font-medium text-gray-500 transition-colors hover:text-[#DB3725]" data-id="element-1645" data-mp="2yam7mv2q8gkt">
            Clear notes
          </button>}
        {showHeadcount && <span className="inline-flex items-center gap-1 text-xs text-gray-600" data-id="element-1646" data-mp="3lxu911x1b5hk">
            About {liveRead.headcount} people mentioned
            <button type="button" onClick={() => setDismissedHeadcount(liveRead.headcount)} aria-label="Dismiss the detected headcount" className="rounded p-0.5 text-gray-400 transition-colors hover:text-gray-700" data-id="element-1647" data-mp="3gzaon5y3a1rb">
              <X size={12} data-id="element-1648" data-mp="20lsv3kfme6pp" />
            </button>
          </span>}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-1649" data-mp="1wkqt4vubjgk1">
        <Field label="Who is this for?" hint={fromNotesHint} data-id="element-1650" data-mp="1mfqu6ifgd50m">
          <SelectDropdown ariaLabel="Who is this for?" size="sm" fullWidth value={audience} onChange={value => onAudienceChange(value as AudienceLevel | 'any')} options={[{
          value: 'any',
          label: 'Not sure yet'
        }, ...LEVEL_ORDER.map(level => ({
          value: level,
          label: level === detectedLevel ? `${LEVEL_LABELS[level]} (from notes)` : LEVEL_LABELS[level]
        }))]} data-id="element-1651" data-mp="1mgabpy16xre4" />
        </Field>
        <Field label="Add topics or skills" htmlFor="notes-extra-terms" data-id="element-1652" data-mp="yq6c01r4ibse">
          <div className="relative" data-id="element-1653" data-mp="yqg01jsly8vg">
            <Search size={14} aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" data-id="element-1654" data-mp="2o5dw6qvnh1qq" />
            <input id="notes-extra-terms" value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commitDraft();
            }
          }} onBlur={() => draft.trim() && commitDraft()} placeholder="Optional: type and press Enter" className="w-full rounded-lg border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-sm text-gray-900 placeholder:text-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1655" data-mp="2qpaiby9dhrco" />
          </div>
        </Field>
      </div>

      {(notes.trim().length > 0 || activeTerms.length > 0) && <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-3" aria-label="Topics in this search" data-id="element-1656" data-mp="3t40zq2wjiass">
          <span className="mr-1 text-xs text-gray-500" data-id="element-1657" data-mp="3obzrcs2ht481">Matching on:</span>
          {activeTerms.map(term => {
        const detected = detectedTerms.includes(term);
        return <span key={term} className="inline-flex items-center gap-1 rounded bg-[#30718D]/10 py-1 pl-2 pr-1 text-xs font-medium text-[#30718D]" data-id="element-1658" data-mp="4io240cv3idk">
                {detected && <Sparkles size={11} aria-label="From notes" data-id="element-1659" data-mp="ehn0mq3wxxmu" />}
                {term}
                <button type="button" onClick={() => onToggleTerm(term)} aria-label={`Remove ${term}`} className="rounded p-0.5 transition-colors hover:bg-[#30718D]/15 focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1660" data-mp="ihe0rkfbxq41">
                  <X size={12} aria-hidden="true" data-id="element-1661" data-mp="x61x3sx023v1" />
                </button>
              </span>;
      })}
          {activeTerms.length === 0 && <span className="text-xs text-gray-500" data-id="element-1662" data-mp="168jhe1t3gmfe">nothing yet</span>}
          {suggestedTerms.map(term => <button key={term} type="button" onClick={() => onToggleTerm(term)} aria-label={`Add ${term}`} className="inline-flex items-center gap-1 rounded border border-dashed border-[#30718D]/40 bg-white py-1 pl-1.5 pr-2 text-xs font-medium text-[#30718D] transition-colors hover:border-[#30718D] hover:bg-[#30718D]/5" data-id="element-1663" data-mp="1rgvk39yptp1a">
              <Plus size={12} aria-hidden="true" data-id="element-1664" data-mp="18s8dgn0w4rll" />
              {term}
            </button>)}
        </div>}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3" data-id="element-1665" data-mp="1ihrwbsolslgg">
        <button type="button" onClick={onBrowseAll} className="text-sm font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1666" data-mp="1drbiw8ks8zu3">
          Browse all skill modules instead
        </button>
        <ResetFiltersButton label="Reset search" isActive={hasSearch} onReset={onReset} data-id="element-1667" data-mp="l1l2ttzlbnvc" />
      </div>
    </div>;
}